"use client";

import { useState, useRef, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useLocale, useTranslations } from 'next-intl';
import { fetchWithAuth } from '@/lib/fetch-with-auth';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import {
  Scale, TrendingUp, Megaphone, Music2, BookOpen, Palette, Film, Briefcase,
  RefreshCw, Send, Bot, User, Loader2, Wand2, Check, Undo2, X,
  ExternalLink, AlertTriangle, Percent, UserPlus,
} from 'lucide-react';
import type { AdvisorDomain } from '@/app/api/ai/creator-advisor/route';

type Params = Record<string, string | number>;

/** Server-built preview for credit/split/editor actions (lib/copilot-tools.ts ActionPreview). */
interface CopilotPreview {
  kind: 'split' | 'contributor' | 'editor';
  name?: string;
  track?: string;
  percent?: number;
  shareType?: 'sales' | 'writing' | 'publishing' | 'performance';
  roles?: string[];
  instrument?: string;
  url?: string;
  section?: string;
  notes?: { code: string; params?: Params }[];
}

/** One allowlisted change proposed by Copilot — validated again server-side on Apply. */
interface CopilotPlanAction {
  tool: string;
  args: Record<string, any>;
  description?: string;
  cost: number;
  preview?: CopilotPreview;
}

/** A proposed change the server refused at plan time, with why. */
interface CopilotRefusal {
  tool: string;
  description?: string;
  code: string;
  params?: Params;
  reason: string;
}

interface CopilotResult { tool: string; ok: boolean; message: string; code?: string; params?: Params; url?: string }

interface Message {
  role: 'user' | 'assistant';
  content: string;
  /** Copilot proposal attached to an assistant message (propose → preview → apply). */
  proposal?: CopilotPlanAction[];
  refused?: CopilotRefusal[];
  proposalCost?: number;
  applied?: boolean;
  dismissed?: boolean;
  undoId?: string;
  undone?: boolean;
  results?: CopilotResult[];
  resultLines?: string[];
}

const TOOL_FN_LABEL: Record<string, string> = {
  updateWork: 'updateWork',
  updateMarketplaceItem: 'updateMarketplaceItem',
  createProfilePost: 'createProfilePost',
  masterTrack: 'masterTrack',
  createParty: 'createParty',
  generateArtwork: 'generateArtwork',
  addContributor: 'addContributor',
  setSplit: 'setSplit',
  openEditor: 'openEditor',
};

/** `bb-copilot` requests already sent — survives the tab remounting this component. */
const handledRequests = new Set<number>();

interface DomainConfig {
  id: AdvisorDomain;
  label: string;
  icon: React.ElementType;
  color: string;
  description: string;
}

const DOMAINS: DomainConfig[] = [
  { id: 'copilot',      label: 'Copilot',         icon: Wand2,      color: 'bg-emerald-600', description: 'Tell BB what to change — retag, reprice, post, master. You review the plan, then tap Apply.' },
  { id: 'music',        label: 'Music',           icon: Music2,     color: 'bg-amber-600',  description: 'Anything music — points you to BB humans who can help' },
  { id: 'artwork',      label: 'Art Work',        icon: Palette,    color: 'bg-rose-600',   description: 'Covers, merch art — human touch beats pure AI' },
  { id: 'film-making',  label: 'Film Making',     icon: Film,       color: 'bg-indigo-600', description: 'Music video, lyric video — human eye, AI as power tool' },
  { id: 'publishing',   label: 'Publishing',      icon: BookOpen,   color: 'bg-pink-600',   description: 'PROs, splits, IPI / ISRC — BB tools replace the publisher' },
  { id: 'legal',        label: 'Legal',           icon: Scale,      color: 'bg-blue-600',   description: 'Contracts, copyright — AI handles 80% at a fraction of cost' },
  { id: 'finance',      label: 'Finance',         icon: TrendingUp, color: 'bg-green-600',  description: 'Revenue, tax, splits — AI + BB tools replace the accountant' },
  { id: 'marketing',    label: 'Promo + Marketing', icon: Megaphone, color: 'bg-purple-600', description: 'Releases, social, press — BB Spread replaces the agency' },
  { id: 'business',     label: 'Business',        icon: Briefcase,  color: 'bg-orange-600', description: 'Labels, management, ops — BB tools run the back office' },
];

const STARTER_QUESTIONS: Record<string, string[]> = {
  copilot: [
    'Make my latest track name-your-price with a $2 floor.',
    'Retag one of my tracks as UK Garage — keep the old genre too.',
    'Post on my wall that the new single is out, keep it short.',
    'Master my newest track for streaming, a little brighter.',
  ],
  music: [
    'Help me find a vocalist on BB for a hip-hop track in Swedish.',
    'I\'m stuck on a bridge — chord ideas in C minor?',
    'Suggest a producer on BB who works with reggae and dub.',
  ],
  artwork: [
    'Which BB designers do reggae / dancehall covers?',
    'Should I commission a human cover or use AI? Pros and cons.',
    'How do I brief a designer so I get something I actually like?',
  ],
  'film-making': [
    'Find me a videographer on BB for a Stockholm music video.',
    'What\'s a realistic budget for a 1-day music video shoot in Sweden?',
    'How should I split a video between human-shot footage and AI scenes?',
  ],
  publishing: [
    'Walk me through registering with STIM as both writer and publisher.',
    'How do I self-publish — and when is paying a publisher actually worth it?',
    'Co-writing a song with two people — how should we split publishing?',
  ],
  legal: [
    'Review this sample-clearance clause for me (paste it).',
    'I\'m signing my first management contract — what are the red flags?',
    'When is this small enough that I don\'t need a lawyer at all?',
  ],
  finance: [
    'What does a Spotify stream actually pay me after all the cuts?',
    'I made €4,200 last year on music in Sweden — what do I owe in tax?',
    'Help me set up bookkeeping using BB\'s tools so I stop dreading tax season.',
  ],
  marketing: [
    'Build me a 4-week release plan for a single using Spread.',
    'How do I pitch Spotify editorial playlists in 2026?',
    'When is hiring a PR agency worth it vs. doing it myself with BB?',
  ],
  business: [
    'Should I incorporate (AB / enskild firma) — what fits an indie creator?',
    'When do I actually need a manager? What should the deal look like?',
    'Help me draft a deal with a producer who\'ll take 30% of master rights.',
  ],
  // legacy alias — same starters as business
  'music-business': [
    'When does an indie creator actually need a manager?',
    'How do I structure a deal with a producer taking master rights?',
    'Should I incorporate (AB / enskild firma) for my music income?',
  ],
};

interface CreatorAdvisorProps {
  /** compact: slim view for chat sidebar; full: standalone page */
  variant?: 'compact' | 'full';
  className?: string;
  /** A prompt sent from outside (the `bb-copilot` event — coπ voice): switches to
   *  Copilot and sends it as the user's message. `id` makes each request unique. */
  request?: { prompt: string; id: number } | null;
  onRequestHandled?: () => void;
}

export function CreatorAdvisor({ variant = 'full', className, request, onRequestHandled }: CreatorAdvisorProps) {
  const tc = useTranslations('copilot');
  const locale = useLocale();
  const router = useRouter();
  const [selectedDomain, setSelectedDomain] = usePersistedState<AdvisorDomain>('creator-ai-domain', 'general');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDomains, setShowDomains] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  /** Go to an editor screen. On phones the chat covers the page, so it steps aside. */
  const openEditorUrl = useCallback((url: string) => {
    if (!url.startsWith('/')) return; // in-app paths only
    router.push(url);
    // Same page (already on /media) gets no route change — tell it directly.
    window.dispatchEvent(new CustomEvent('bb-open-editor', { detail: { url } }));
    window.dispatchEvent(new Event('close-sidebar-and-chat'));
  }, [router]);

  const sendMessage = useCallback(async (text: string, domainOverride?: AdvisorDomain) => {
    if (!text.trim() || isLoading) return;
    const domain = domainOverride || selectedDomain;
    const userMsg: Message = { role: 'user', content: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setShowDomains(false);

    try {
      if (domain === 'copilot') {
        // BB Copilot — propose → preview → apply. The reply may carry a plan
        // of allowlisted changes; nothing is written until Apply is tapped.
        const res = await fetchWithAuth('/api/ai/copilot', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: text.trim(),
            history: messages.slice(-6).map(m => ({ role: m.role, content: m.content })),
            locale,
          }),
        });
        const data = await res.json();
        const actions: CopilotPlanAction[] = Array.isArray(data.actions) ? data.actions : [];
        const refused: CopilotRefusal[] = Array.isArray(data.refused) ? data.refused : [];
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.reply || 'Sorry, I could not work that one out.',
          proposal: actions.length > 0 ? actions : undefined,
          refused: refused.length > 0 ? refused : undefined,
          proposalCost: data.totalCost || 0,
        }]);
        // "Open the credits of X" — a plan that only opens one editor needs no
        // Apply: it writes nothing and costs nothing, so go straight there.
        const only = actions.length === 1 && refused.length === 0 ? actions[0] : null;
        if (only?.tool === 'openEditor' && only.preview?.url) {
          const url = only.preview.url;
          setTimeout(() => openEditorUrl(url), 700);
        }
        return;
      }
      const res = await fetchWithAuth('/api/ai/creator-advisor', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: text.trim(),
          domain: selectedDomain,
          conversationHistory: messages.slice(-6).map(m => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await res.json();
      const assistantMsg: Message = { role: 'assistant', content: data.answer || 'Sorry, I could not generate a response.' };
      setMessages(prev => [...prev, assistantMsg]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'I\'m having trouble connecting right now. Please try again in a moment.' }]);
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isLoading, messages, selectedDomain, openEditorUrl, locale]);

  // An outside request (coπ) → Copilot domain + send. Held while a reply is
  // still loading; each request id is sent once even if the tab remounts.
  useEffect(() => {
    if (!request || handledRequests.has(request.id)) return;
    if (selectedDomain !== 'copilot') { setSelectedDomain('copilot'); setShowDomains(false); }
    if (isLoading) return;
    handledRequests.add(request.id);
    sendMessage(request.prompt, 'copilot');
    onRequestHandled?.();
  }, [request, isLoading, selectedDomain, setSelectedDomain, sendMessage, onRequestHandled]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const resetConversation = () => {
    setMessages([]);
    setShowDomains(true);
    setInput('');
  };

  // ── Copilot plan actions ──────────────────────────────────────────────────
  const [applyingIndex, setApplyingIndex] = useState<number | null>(null);
  const [undoingIndex, setUndoingIndex] = useState<number | null>(null);

  const patchMessage = (index: number, patch: Partial<Message>) =>
    setMessages(prev => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));

  const applyPlan = async (index: number) => {
    const msg = messages[index];
    // openEditor rows have their own Open button — Apply only sends real changes.
    const writes = (msg?.proposal || []).filter(a => a.tool !== 'openEditor');
    if (!writes.length || msg.applied || msg.dismissed || applyingIndex !== null) return;
    setApplyingIndex(index);
    try {
      const res = await fetchWithAuth('/api/ai/copilot/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actions: writes }),
      });
      const data = await res.json();
      if (!res.ok) {
        // data.message is already human (e.g. the 402 shortfall); raw server
        // errors get friendly copy instead of "apply failed".
        patchMessage(index, { resultLines: [data.message || "Apply didn't complete — please try again."] });
        return;
      }
      patchMessage(index, {
        applied: true,
        undoId: data.undoId,
        results: Array.isArray(data.results) ? data.results : [],
      });
    } catch {
      patchMessage(index, { resultLines: ['Apply failed — connection problem.'] });
    } finally {
      setApplyingIndex(null);
    }
  };

  const undoPlan = async (index: number) => {
    const msg = messages[index];
    if (!msg?.undoId || msg.undone || undoingIndex !== null) return;
    setUndoingIndex(index);
    try {
      const res = await fetchWithAuth('/api/ai/copilot/undo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ undoId: msg.undoId }),
      });
      const data = await res.json();
      patchMessage(index, {
        undone: res.ok,
        resultLines: res.ok ? (data.results || []).map((r: string) => `↩ ${r}`) : [data.error || 'Undo failed'],
      });
    } catch {
      patchMessage(index, { resultLines: ['Undo failed — connection problem.'] });
    } finally {
      setUndoingIndex(null);
    }
  };

  const activeDomain = DOMAINS.find(d => d.id === selectedDomain);

  // ── Plan-card copy, in the user's language (codes come from copilot-tools) ──
  const fmtNum = (n: unknown) => (typeof n === 'number' ? String(Math.round(n * 100) / 100) : String(n ?? ''));
  const shareLabel = (type: unknown) =>
    typeof type === 'string' && tc.has(`shareType.${type}`) ? tc(`shareType.${type}`) : String(type ?? '');
  const sayParams = (p?: Params) =>
    Object.fromEntries(Object.entries(p || {}).map(([k, v]) => [k, k === 'type' ? shareLabel(v) : fmtNum(v)]));
  const say = (group: 'note' | 'refuse' | 'result', code: string | undefined, p: Params | undefined, fallback: string) =>
    code && tc.has(`${group}.${code}`) ? tc(`${group}.${code}`, sayParams(p)) : fallback;
  const actionLine = (a: CopilotPlanAction): string => {
    const p = a.preview;
    if (p?.kind === 'split') {
      return tc('splitLine', { name: p.name || '', percent: fmtNum(p.percent), type: shareLabel(p.shareType), track: p.track || '' });
    }
    if (p?.kind === 'contributor') {
      return tc('contributorLine', { name: p.name || '', roles: (p.roles || []).join(', '), track: p.track || '' })
        + (p.instrument ? ` · ${tc('instrument', { instrument: p.instrument })}` : '');
    }
    if (p?.kind === 'editor') {
      const section = ['details', 'credits', 'splits', 'lyrics'].includes(p.section || '') ? p.section! : 'details';
      return tc('editorLine', { section: tc(`section.${section}`), track: p.track || '' });
    }
    return a.description || a.tool;
  };
  const toolIcon = (tool: string) => {
    if (tool === 'setSplit') return <Percent className="h-3 w-3 text-amber-500" aria-hidden />;
    if (tool === 'addContributor') return <UserPlus className="h-3 w-3 text-emerald-400" aria-hidden />;
    if (tool === 'openEditor') return <ExternalLink className="h-3 w-3 text-emerald-400" aria-hidden />;
    return <span className="text-emerald-400 font-mono text-xs" aria-hidden>
      {tool === 'createProfilePost' ? '+' : tool === 'masterTrack' ? '♫' : '±'}
    </span>;
  };

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Domain Selector — "signage" parallel to Support's header: same
          position and weight, different job (career advice vs. app help). */}
      <div className="px-3 pt-3 pb-2 border-b border-border/60">
        <div className="flex items-center justify-between mb-2">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <Bot className="h-3.5 w-3.5 text-primary" /> Career &amp; Industry Advice
          </span>
          {messages.length > 0 && (
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={resetConversation}>
              <RefreshCw className="h-3 w-3 mr-1" />New
            </Button>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {DOMAINS.map(d => {
            const Icon = d.icon;
            const isActive = selectedDomain === d.id;
            return (
              <button
                key={d.id}
                onClick={() => { setSelectedDomain(d.id); setShowDomains(true); }}
                className={cn(
                  'flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-all',
                  isActive
                    ? cn(d.color, 'text-white shadow-sm')
                    : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
                )}
              >
                <Icon className="h-3 w-3" />
                {d.label}
              </button>
            );
          })}
        </div>
        {activeDomain && (
          <p className="text-xs text-muted-foreground mt-1.5">{activeDomain.description}</p>
        )}
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-3 py-2" ref={scrollRef as any}>
        {messages.length === 0 ? (
          showDomains && (
            <div className="space-y-3 py-2">
              <p className="text-xs text-muted-foreground text-center">
                {activeDomain
                  ? `Ask me anything about ${activeDomain.label.toLowerCase()} for music creators`
                  : 'Choose a domain above and ask your question'}
              </p>
              {selectedDomain && STARTER_QUESTIONS[selectedDomain] && (
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-muted-foreground">Suggested questions:</p>
                  {(selectedDomain === 'copilot'
                    ? [tc('starterCredits'), tc('starterEditor'), ...STARTER_QUESTIONS.copilot]
                    : STARTER_QUESTIONS[selectedDomain]).map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(q)}
                      className="w-full text-left text-xs p-2 rounded bg-muted/60 hover:bg-muted text-foreground/80 hover:text-foreground transition-colors border border-border/40 hover:border-border"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        ) : (
          <div className="space-y-3 py-1">
            {messages.map((msg, i) => (
              <div key={i} className={cn('flex gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                {msg.role === 'assistant' && (
                  <div className={cn('h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5', activeDomain?.color || 'bg-primary')}>
                    <Bot className="h-3.5 w-3.5 text-white" />
                  </div>
                )}
                <div
                  className={cn(
                    'max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed',
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-tr-sm'
                      : 'bg-muted text-foreground rounded-tl-sm'
                  )}
                >
                  {msg.content}
                  {/* ── Copilot plan card: propose → preview → apply ── */}
                  {(msg.proposal || msg.refused) && (() => {
                    const plan = msg.proposal || [];
                    const writes = plan.filter(a => a.tool !== 'openEditor');
                    // Someone's money share changes → amber card + an explicit warning
                    // above Apply. Never auto-applied.
                    const hasSplit = writes.some(a => a.tool === 'setSplit');
                    const cost = msg.proposalCost || 0;
                    return (
                    <div className={cn(
                      'mt-2 rounded-lg border-2 bg-background/70 overflow-hidden',
                      hasSplit ? 'border-amber-500/80' : 'border-emerald-500/60',
                    )}>
                      {plan.length > 0 && (
                        <div className="px-2.5 py-1.5 border-b border-border/50 flex items-center gap-1.5">
                          <Wand2 className="h-3 w-3 text-emerald-400" aria-hidden />
                          <span className="text-3xs font-semibold uppercase tracking-wider">
                            {tc('proposedChanges', { count: plan.length })}
                          </span>
                        </div>
                      )}
                      {plan.map((a, ai) => (
                        <div key={ai} className="px-2.5 py-1.5 border-b border-border/30 flex items-start gap-2">
                          <span className="mt-0.5 shrink-0">{toolIcon(a.tool)}</span>
                          <div className="flex-grow min-w-0">
                            <p className={cn('text-xs leading-snug', a.tool === 'setSplit' && 'font-semibold')}>{actionLine(a)}</p>
                            {a.preview?.notes?.map((n, ni) => (
                              <p key={ni} className="text-3xs text-muted-foreground leading-snug mt-0.5">{say('note', n.code, n.params, '')}</p>
                            ))}
                          </div>
                          {a.tool === 'openEditor' && a.preview?.url ? (
                            <Button
                              size="sm" variant="outlineContrast"
                              className="h-7 text-xs gap-1 shrink-0"
                              onClick={() => openEditorUrl(a.preview!.url!)}
                            >
                              <ExternalLink className="h-3 w-3" aria-hidden /> {tc('open')}
                            </Button>
                          ) : (
                            <span className="text-4xs font-mono text-muted-foreground shrink-0 mt-0.5">
                              {TOOL_FN_LABEL[a.tool] || a.tool}
                            </span>
                          )}
                        </div>
                      ))}
                      {msg.refused && msg.refused.length > 0 && (
                        <>
                          <div className="px-2.5 py-1.5 border-b border-border/50 flex items-center gap-1.5">
                            <X className="h-3 w-3 text-destructive" aria-hidden />
                            <span className="text-3xs font-semibold uppercase tracking-wider">
                              {tc('refusedTitle', { count: msg.refused.length })}
                            </span>
                          </div>
                          {msg.refused.map((r, ri) => (
                            <div key={ri} className="px-2.5 py-1.5 border-b border-border/30">
                              {r.description && <p className="text-xs leading-snug line-through decoration-destructive/70">{r.description}</p>}
                              <p className="text-3xs text-foreground leading-snug mt-0.5">{say('refuse', r.code, r.params, r.reason)}</p>
                            </div>
                          ))}
                        </>
                      )}
                      {hasSplit && !msg.applied && !msg.dismissed && (
                        <div className="px-2.5 py-1.5 border-b border-border/30 flex items-start gap-1.5 bg-amber-500/10">
                          <AlertTriangle className="h-3 w-3 text-amber-500 mt-0.5 shrink-0" aria-hidden />
                          <p className="text-3xs text-foreground leading-snug">{tc('splitWarning')}</p>
                        </div>
                      )}
                      {writes.length > 0 && (
                      <div className="px-2.5 py-1.5 flex items-center gap-2 flex-wrap">
                        {!msg.applied && !msg.dismissed && (
                          <>
                            <Button
                              size="sm"
                              className="h-7 text-xs gap-1 bg-emerald-600 text-white hover:bg-emerald-500"
                              disabled={applyingIndex !== null}
                              onClick={() => applyPlan(i)}
                            >
                              {applyingIndex === i ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                              {tc('apply')}
                            </Button>
                            <Button
                              size="sm" variant="outlineContrast"
                              className="h-7 text-xs gap-1"
                              disabled={applyingIndex !== null}
                              onClick={() => patchMessage(i, { dismissed: true })}
                            >
                              <X className="h-3 w-3" /> {tc('dismiss')}
                            </Button>
                            <span className="ml-auto text-3xs font-mono text-muted-foreground">
                              {cost > 0 ? tc('tokens', { count: cost }) : tc('free')}
                            </span>
                          </>
                        )}
                        {msg.applied && !msg.undone && (
                          <>
                            <span className="text-3xs font-semibold text-emerald-400">{tc('applied')}</span>
                            {msg.undoId && (
                              <Button
                                size="sm" variant="outlineContrast"
                                className="h-7 text-xs gap-1"
                                disabled={undoingIndex !== null}
                                onClick={() => undoPlan(i)}
                              >
                                {undoingIndex === i ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />}
                                {tc('undo')}
                              </Button>
                            )}
                          </>
                        )}
                        {msg.undone && <span className="text-3xs text-muted-foreground">{tc('undone')}</span>}
                        {msg.dismissed && <span className="text-3xs text-muted-foreground">{tc('dismissed')}</span>}
                      </div>
                      )}
                      {((msg.results && msg.results.length > 0) || (msg.resultLines && msg.resultLines.length > 0)) && (
                        <div className="px-2.5 pb-1.5 space-y-0.5">
                          {msg.results?.map((r, li) => (
                            <p key={`r${li}`} className="text-3xs text-muted-foreground leading-snug">
                              {r.ok ? '✓' : '✗'} {r.ok ? say('result', r.code, r.params, r.message) : say('refuse', r.code, r.params, r.message)}
                            </p>
                          ))}
                          {msg.resultLines?.map((l, li) => (
                            <p key={`l${li}`} className="text-3xs text-muted-foreground leading-snug">{l}</p>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                  })()}
                </div>
                {msg.role === 'user' && (
                  <div className="h-6 w-6 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="h-3.5 w-3.5" />
                  </div>
                )}
              </div>
            ))}
            {isLoading && (
              <div className="flex gap-2 justify-start">
                <div className={cn('h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0', activeDomain?.color || 'bg-primary')}>
                  <Bot className="h-3.5 w-3.5 text-white" />
                </div>
                <div className="bg-muted rounded-xl rounded-tl-sm px-3 py-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {/* Legal disclaimer for legal domain */}
      {selectedDomain === 'legal' && messages.length > 0 && (
        <div className="px-3 py-1">
          <p className="text-[10px] text-muted-foreground bg-muted/50 rounded px-2 py-1">
            ⚠️ AI advice only — not a substitute for qualified legal counsel.
          </p>
        </div>
      )}
      {selectedDomain === 'finance' && messages.length > 0 && (
        <div className="px-3 py-1">
          <p className="text-[10px] text-muted-foreground bg-muted/50 rounded px-2 py-1">
            ⚠️ AI guidance only — consult a certified financial advisor for major decisions.
          </p>
        </div>
      )}

      {/* Copilot runs on BB tokens — the upsell surface (Phase 07). */}
      {selectedDomain === 'copilot' && (
        <div className="px-3 py-1">
          <p className="text-[10px] text-muted-foreground bg-muted/50 rounded px-2 py-1">
            Copilot runs on BB tokens — 2 per question, 1 per applied change, 2 to master, 15 for artwork. Pay as you go, no subscription.{' '}
            <Link href="/tokens" className="text-primary underline underline-offset-2">Top up</Link>.
          </p>
        </div>
      )}

      {/* Input */}
      <div className="px-3 pb-3 pt-2 border-t border-border/60">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={selectedDomain === 'copilot'
              ? 'Tell Copilot what to change…'
              : `Ask about ${activeDomain?.label.toLowerCase() || 'music industry'}…`}
            className="flex-1 h-9 text-sm"
            disabled={isLoading}
          />
          <Button
            size="icon"
            className="h-9 w-9"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || isLoading}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
